import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { ModernButton } from '../../components/ui/ModernButton';
import { Toast } from 'primereact/toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ActivityService } from '../../services/activityService';
import { Activity, ActivityType, EntityType } from '../../types/activity';
import './RecentActivitiesPage.css';

const getDefaultDateRange = (): Date[] => [
	new Date(new Date().setDate(new Date().getDate() - 30)),
	new Date()
];

export const RecentActivitiesPage: React.FC = () => {
	const { currentAgent } = useAuth();
	const navigate = useNavigate();
	const toast = useRef<Toast>(null);

	const [activities, setActivities] = useState<Activity[]>([]);
	const [loading, setLoading] = useState(true);
	const [totalRecords, setTotalRecords] = useState(0);

	// Filters
	const [activityTypeFilter, setActivityTypeFilter] = useState<ActivityType | 'all'>('all');
	const [entityTypeFilter, setEntityTypeFilter] = useState<EntityType | 'all'>('all');
	const [searchTerm, setSearchTerm] = useState('');
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
	// What the Calendar displays (may be a half-picked range) vs. what the query uses
	const [dateRange, setDateRange] = useState<(Date | null)[] | null>(getDefaultDateRange);
	const [appliedDateRange, setAppliedDateRange] = useState<Date[] | null>(getDefaultDateRange);

	const activityTypeOptions = [
		{ label: 'All Activities', value: 'all' },
		{ label: 'Customer Viewed', value: 'customer_viewed' },
		{ label: 'Customer Edited', value: 'customer_edited' },
		{ label: 'Insight Viewed', value: 'insight_viewed' },
		{ label: 'Insight Status Changed', value: 'insight_status_changed' },
		{ label: 'Notification Viewed', value: 'notification_viewed' },
		{ label: 'Agent Viewed', value: 'agent_viewed' }
	];

	const entityTypeOptions = [
		{ label: 'All Types', value: 'all' },
		{ label: 'Customers', value: 'customer' },
		{ label: 'Insights', value: 'insight' },
		{ label: 'Notifications', value: 'notification' },
		{ label: 'Agents', value: 'agent' }
	];

	const loadActivities = useCallback(async () => {
		if (!currentAgent?.id) return;

		setLoading(true);
		try {
			const filters: any = {};

			if (activityTypeFilter !== 'all') {
				filters.activityType = activityTypeFilter;
			}

			if (entityTypeFilter !== 'all') {
				filters.entityType = entityTypeFilter;
			}

			if (debouncedSearchTerm) {
				filters.searchTerm = debouncedSearchTerm;
			}

			if (appliedDateRange && appliedDateRange.length === 2) {
				filters.startDate = appliedDateRange[0];
				filters.endDate = appliedDateRange[1];
			}

			filters.limit = 100; // Show up to 100 activities

			const result = await ActivityService.getAllActivities(currentAgent.id, filters);
			setActivities(result.activities);
			setTotalRecords(result.total);
		} catch (error) {
			console.error('Error loading activities:', error);
			toast.current?.show({
				severity: 'error',
				summary: 'Error',
				detail: 'Failed to load activities',
				life: 3000
			});
		} finally {
			setLoading(false);
		}
	}, [currentAgent?.id, activityTypeFilter, entityTypeFilter, debouncedSearchTerm, appliedDateRange]);

	useEffect(() => {
		loadActivities();
	}, [loadActivities]);

	// Debounce the search box so typing does not re-query on every keystroke
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
		}, 300);

		return () => clearTimeout(timer);
	}, [searchTerm]);

	// A range Calendar emits [start, null] after the first click; only apply the
	// range once both ends are picked, or when the range has been cleared.
	const handleDateRangeChange = (value: (Date | null)[] | null | undefined) => {
		setDateRange(value ?? null);

		if (!value || value.length === 0) {
			setAppliedDateRange(null);
			return;
		}

		const [start, end] = value;
		if (start && end) {
			setAppliedDateRange([start, end]);
		}
	};

	// Agent activities have no destination in the agent portal, so they are not clickable
	const isActivityNavigable = (activity: Activity) => activity.entityType !== 'agent';

	const handleActivityClick = (activity: Activity) => {
		// Navigate to the appropriate page based on entity type
		switch (activity.entityType) {
			case 'customer':
				navigate(`/agent/customers/${activity.entityId}`);
				break;
			case 'insight':
				navigate(`/agent/insights`);
				break;
			case 'notification':
				navigate(`/agent/notifications`);
				break;
			default:
				break;
		}
	};

	const iconTemplate = (activity: Activity) => {
		const { icon, color } = ActivityService.getActivityIcon(activity.activityType);
		return (
			<div className="activity-icon-cell" style={{ backgroundColor: color }}>
				<i className={`pi ${icon}`}></i>
			</div>
		);
	};

	const activityTypeTemplate = (activity: Activity) => {
		return (
			<span className="activity-type-label">
				{activity.activityType.split('_').map(word => 
					word.charAt(0).toUpperCase() + word.slice(1)
				).join(' ')}
			</span>
		);
	};

	const entityTypeTemplate = (activity: Activity) => {
		const entityTypeLabels: Record<EntityType, string> = {
			customer: 'Customer',
			insight: 'Insight',
			notification: 'Notification',
			agent: 'Agent'
		};
		return <span>{entityTypeLabels[activity.entityType]}</span>;
	};

	const descriptionTemplate = (activity: Activity) => {
		return (
			<div className="activity-description">
				{ActivityService.formatActivityDescription(activity)}
			</div>
		);
	};

	const timestampTemplate = (activity: Activity) => {
		const now = Date.now();
		const activityTime = activity.timestamp.getTime();
		const diffMinutes = Math.floor((now - activityTime) / (1000 * 60));

		let timeAgo = '';
		if (diffMinutes < 60) {
			timeAgo = `${diffMinutes}m ago`;
		} else {
			const diffHours = Math.floor(diffMinutes / 60);
			if (diffHours < 24) {
				timeAgo = `${diffHours}h ago`;
			} else {
				const diffDays = Math.floor(diffHours / 24);
				timeAgo = `${diffDays}d ago`;
			}
		}

		return (
			<div className="activity-timestamp">
				<span className="time-ago">{timeAgo}</span>
				<span className="full-date">{activity.timestamp.toLocaleString()}</span>
			</div>
		);
	};

	const actionsTemplate = (activity: Activity) => {
		if (!isActivityNavigable(activity)) return null;

		return (
			<ModernButton
				variant="ghost"
				size="sm"
				icon="pi pi-arrow-right"
				onClick={() => handleActivityClick(activity)}
			>
				View
			</ModernButton>
		);
	};

	const header = (
		<div className="activities-header">
			<div className="header-left">
				<h2>Recent Activity</h2>
				<p className="subtitle">Track your interactions with customers, insights, and more</p>
			</div>
		</div>
	);

	return (
		<div className="recent-activities-page">
			<Toast ref={toast} />

			<Card className="activities-card">
				{header}

				<div className="activities-filters">
					<div className="filter-row">
						<div className="filter-item">
							<label>Activity Type</label>
							<Dropdown
								value={activityTypeFilter}
								options={activityTypeOptions}
								onChange={(e) => setActivityTypeFilter(e.value)}
								placeholder="Select Activity Type"
								className="activity-type-filter"
							/>
						</div>

						<div className="filter-item">
							<label>Entity Type</label>
							<Dropdown
								value={entityTypeFilter}
								options={entityTypeOptions}
								onChange={(e) => setEntityTypeFilter(e.value)}
								placeholder="Select Entity Type"
								className="entity-type-filter"
							/>
						</div>

						<div className="filter-item">
							<label>Date Range</label>
							<Calendar
								value={dateRange}
								onChange={(e) => handleDateRangeChange(e.value)}
								selectionMode="range"
								showButtonBar
								readOnlyInput
								showIcon
								placeholder="Select Date Range"
								className="date-range-filter"
							/>
						</div>

						<div className="filter-item search-item">
							<label>Search</label>
							<span className="p-input-icon-left">
								<i className="pi pi-search" />
								<InputText
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									placeholder="Search by name..."
									className="search-filter"
								/>
							</span>
						</div>
					</div>
				</div>

				<DataTable
					value={activities}
					loading={loading}
					emptyMessage={
						<div className="empty-state">
							<i className="pi pi-history" style={{ fontSize: '3rem', color: '#95a5a6' }}></i>
							<h3>No Activities Yet</h3>
							<p>Your interactions will appear here</p>
						</div>
					}
					paginator
					rows={20}
					rowsPerPageOptions={[10, 20, 50]}
					totalRecords={totalRecords}
					className="activities-table"
					onRowClick={(e) => {
						const activity = e.data as Activity;
						if (isActivityNavigable(activity)) {
							handleActivityClick(activity);
						}
					}}
					rowClassName={(activity: Activity) => (isActivityNavigable(activity) ? '' : 'activity-row-static')}
					rowHover
				>
					<Column
						body={iconTemplate}
						style={{ width: '60px' }}
						className="icon-column"
					/>
					<Column
						field="activityType"
						header="Activity"
						body={activityTypeTemplate}
						style={{ width: '150px' }}
					/>
					<Column
						field="entityType"
						header="Type"
						body={entityTypeTemplate}
						style={{ width: '120px' }}
					/>
					<Column
						header="Description"
						body={descriptionTemplate}
						style={{ minWidth: '300px' }}
					/>
					<Column
						field="timestamp"
						header="When"
						body={timestampTemplate}
						style={{ width: '180px' }}
					/>
					<Column
						body={actionsTemplate}
						style={{ width: '100px' }}
					/>
				</DataTable>
			</Card>
		</div>
	);
};

export default RecentActivitiesPage;

